import {AxiosError} from "axios";
import {single_product_query_key} from "..";
import {message} from "antd";
import {AnalyticsEvent} from "../../../utils/analytics";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {useClientV2} from "../../../utils/hooks";
import {digitalProductsQueryKeys} from "../../../features/digital-products/hooks/query-keys";
import {GetProductApiResponse} from "../types";
import {ProductRenameContentApiPayload} from "./types";

/**
 * Renames one of a digital product's uploaded content files.
 *
 * On v2 `PATCH digital-products/:productId/content/:contentId` with a
 * `{fileName}` body. v1's `PUT products/contents/rename` took the ids and the
 * extension in the body; v2 takes the ids in the path and re-appends the
 * original extension itself when the submitted name drops it, so
 * `fileExtension` is no longer sent (the payload type keeps it, since the shared
 * rename modal has it to hand and the optimistic update reads the same object).
 *
 * The response is the updated content row, but the server may have adjusted the
 * name (extension re-append), so the caches are invalidated rather than patched
 * with the submitted value.
 */
export const useProductRenameContent = () => {
  const queryClient = useQueryClient();

  const client = useClientV2();
  return useMutation({
    mutationFn: (productData: ProductRenameContentApiPayload) =>
      client(
        `digital-products/${productData.productId}/content/${productData.contentId}`,
        {
          method: "PATCH",
          data: {fileName: productData.fileName},
        }
      ),
    onMutate: (values) => {
      queryClient.cancelQueries([single_product_query_key, String(values.productId)]);

      const oldProductData = queryClient.getQueryData<GetProductApiResponse>([
        single_product_query_key,
        String(values.productId),
      ]);

      const newProductContentMedia = oldProductData?.medias.map((content) => {
        if (content.id === values.contentId) {
          return {
            ...content,
            file_name: values.fileName,
          };
        }
        return content;
      });

      queryClient.setQueryData([single_product_query_key, String(values.productId)], {
        ...oldProductData,
        medias: newProductContentMedia,
      });

      return () =>
        queryClient.setQueryData(
          [single_product_query_key, String(values.productId)],
          oldProductData
        );
    },
    onError: (error: AxiosError<{message: string}>, values, rollback) => {
      message.error(error.message);
      AnalyticsEvent("Digital Products", "Digital products content rename fail");
      if (typeof rollback === "function") {
        rollback();
      }
    },
    onSuccess: (data, values) => {
      AnalyticsEvent("Digital Products", "Digital products content rename success");
    },
    onSettled: (d, e, values) =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: [single_product_query_key, String(values.productId)],
        }),
        // The content tab and the create wizard both read the product's files
        // through the digital-products slice, so that list has to be refreshed
        // as well.
        queryClient.invalidateQueries({
          queryKey: digitalProductsQueryKeys.content(values.productId),
        }),
      ]),
  });
};
